/**
 * Form Submitter
 * Handles form submission orchestration
 */

import { API } from '../utils/api.js';
import { FileUploader } from './file_uploader.js';
import { RateLimiter } from './rate_limiter.js';
import { DateUtils } from '../utils/date_utils.js';

export class FormSubmitter {
  constructor(formId, formState, validator, formElement = null, formConfig = null) {
    this.formId = formId;
    this.formState = formState;
    this.validator = validator;
    /** Form DOM element to collect from (same as engine's getFormElement()); avoids missing rows when form has no id="tqform") */
    this.formElement = formElement;
    /** Form config (fields list) so we can normalize date fields to YYYY-MM-DD for server */
    this.formConfig = formConfig;
  }

  /**
   * Return true if the field (by base name or indexed name) is a date type.
   * @param {string} name - field name (e.g. "field_id" or "field_id[0]")
   * @returns {boolean}
   */
  isDateField(name) {
    if (!this.formConfig || !this.formConfig.fields) return false;
    const baseName = name.replace(/\[\d+\]$/, '').replace(/\[\]$/, '');
    const field = this.formConfig.fields.find(f => f.id === baseName);
    return field && field.type === 'date';
  }

  /**
   * Submit form
   * @param {Object} options - Submission options
   * @returns {Promise<Object>}
   */
  async submit(options = {}) {
    const {
      currentUser = null,
      submissionLimits = {},
      onProgress = null,
      setHiddenFieldDefaults = null
    } = options;

    // Set hidden field defaults
    if (setHiddenFieldDefaults) {
      setHiddenFieldDefaults();
    }

    // Check rate limits
    const rateLimitCheck = RateLimiter.checkRateLimit(submissionLimits, currentUser);
    if (!rateLimitCheck.allowed) {
      throw new Error(rateLimitCheck.error);
    }

    // Engine already ran validateAll() before calling submit().
    // Do not re-check formState here: a 100ms blur validator can mark a
    // field invalid after the submit click (flatpickr altInput, last focused field).

    if (onProgress) onProgress('uploading');

    // Collect form data
    const formData = await this.collectFormData();

    // Ensure draft flag is explicitly false for final submission
    formData.draft = false;

    if (onProgress) onProgress('saving');

    // Submit to API
    const response = await API.saveFormEntry(formData);

    if (response.status !== 'ok') {
      throw new Error(response.error || 'Form submission failed');
    }

    return response;
  }

  /**
   * Collect form data from all fields
   * @returns {Promise<Object>}
   */
  async collectFormData() {
    const formData = {
      form: this.formId,
      entry: {}
    };

    if (this.formState.isEditMode() && this.formState.getEntryId()) {
      formData.entry_id = this.formState.getEntryId();
    }

    // Extract behalf_token from URL query string if present
    const urlParams = new URLSearchParams(window.location.search);
    const behalfToken = urlParams.get('behalf_token');
    if (behalfToken) {
      formData.behalf_token = behalfToken;
    }

    // Handle file uploads
    await this.collectFileData(formData);

    // Collect all other field data
    this.collectFieldData(formData);

    return formData;
  }

  /**
   * Collect file data
   * @param {Object} formData - Form data object
   */
  async collectFileData(formData) {
    const fileInputs = document.querySelectorAll('input[type="file"][name]');
    const uploadPromises = [];

    for (const input of fileInputs) {
      if (!input.name || input.name.trim() === '') continue;
      const fieldId = input.name.replace(/\[\]$/, '');

      // Check for existing file references (edit mode)
      const fileReferences = document.querySelectorAll(`.file_refernce[data-field_id="${input.name}"], .file_refernce[data-field_id="${fieldId}"]`);
      if (fileReferences.length > 0 && input.files.length === 0) {
        // Use existing file references
        fileReferences.forEach(link => {
          const href = link.getAttribute('href');
          if (!href) return;
          if (input.name.endsWith('[]')) {
            if (!formData.entry[fieldId]) {
              formData.entry[fieldId] = [];
            }
            if (!formData.entry[fieldId].includes(href)) {
              formData.entry[fieldId].push(href);
            }
          } else {
            formData.entry[fieldId] = href;
          }
        });
        continue;
      }

      // Upload new files
      if (input.files.length > 0) {
        const files = Array.from(input.files);
        const uploadPromise = FileUploader.uploadFiles(files, input.name).then(results => {
          const fieldId = input.name.replace('[]', '');
          const origKey = 'tq_original_filename_' + fieldId;
          results.forEach((result, index) => {
            if (result.error) {
              return;
            }

            let fileReference = null;
            if (result && result.url) {
              fileReference = result.url;
            } else if (result && result.file_url) {
              fileReference = result.file_url;
            } else if (result && result.fileName && result.fileData) {
              fileReference = result.fileName;
            } else {
              return;
            }

            if (result && result.originalName) {
              if (input.name.endsWith('[]') || files.length > 1) {
                if (!formData.entry[origKey]) {
                  formData.entry[origKey] = [];
                }
                formData.entry[origKey].push(result.originalName);
              } else {
                formData.entry[origKey] = result.originalName;
              }
            }

            if (input.name.endsWith('[]') || files.length > 1) {
              if (!formData.entry[fieldId]) {
                formData.entry[fieldId] = [];
              }
              formData.entry[fieldId].push(fileReference);
            } else {
              formData.entry[fieldId] = fileReference;
            }
          });
        });

        uploadPromises.push(uploadPromise);
      }
    }

    await Promise.all(uploadPromises);
  }

  /**
   * Collect field data
   * @param {Object} formData - Form data object
   */
  collectFieldData(formData) {
    const form = this.formElement || document.getElementById('tqform');
    const formFields = form?.elements || [];

    /** @type {Map<string, Map<number, *>>} indexed repeater: baseName -> (index -> value or array for checkbox) */
    const indexedRepeater = new Map();

    Array.from(formFields).forEach(field => {
      if (!field.name ||
          field.type === 'file' ||
          field.type === 'submit' ||
          field.classList.contains('signature-pad')) {
        return;
      }

      // Skip conditionally hidden fields (d-none on form-group or formCol)
      if (field.closest('.form-group.d-none') || field.closest('.formCol.d-none')) {
        return;
      }

      // Repeater indexed names: field_id[0], field_id[1], etc. -> build field_id: [v0, v1, v2]
      const indexedMatch = field.name.match(/^(.+)\[(\d+)\]$/);
      if (indexedMatch) {
        const baseName = indexedMatch[1];
        const index = parseInt(indexedMatch[2], 10);
        if (!indexedRepeater.has(baseName)) {
          indexedRepeater.set(baseName, new Map());
        }
        const rowMap = indexedRepeater.get(baseName);

        if (field.classList.contains('phone-input') && field.itiInstance) {
          const fullNumber = field.itiInstance.getNumber();
          rowMap.set(index, fullNumber || '');
        } else if (field.type === 'checkbox') {
          if (!rowMap.has(index)) rowMap.set(index, []);
          if (field.checked) rowMap.get(index).push(field.value);
        } else if (field.type === 'radio') {
          if (field.checked) rowMap.set(index, field.value);
        } else {
          let val = field.value || '';
          if (this.isDateField(field.name)) val = DateUtils.toYYYYMMDD(val);
          rowMap.set(index, val);
        }
        return;
      }

      // Handle phone inputs with intl-tel-input (non-indexed)
      if (field.classList.contains('phone-input') && field.itiInstance) {
        const fullNumber = field.itiInstance.getNumber();
        if (fullNumber) {
          if (field.name.endsWith('[]')) {
            const baseName = field.name.replace(/\[\]$/, '');
            if (!formData.entry[baseName]) formData.entry[baseName] = [];
            formData.entry[baseName].push(fullNumber);
          } else {
            formData.entry[field.name] = fullNumber;
          }
        }
        return;
      }

      // Handle array fields (legacy non-indexed repeater / multi-checkbox): store under field_id (no [])
      if (field.name.endsWith('[]')) {
        const baseName = field.name.replace(/\[\]$/, '');
        if (!formData.entry[baseName]) {
          formData.entry[baseName] = [];
        }
        if (field.type === 'checkbox') {
          const checkedFields = document.querySelectorAll(`[name="${field.name}"]:checked`);
          checkedFields.forEach(checked => {
            if (!formData.entry[baseName].includes(checked.value)) {
              formData.entry[baseName].push(checked.value);
            }
          });
        } else {
          let val = field.value;
          if (this.isDateField(baseName)) val = DateUtils.toYYYYMMDD(val);
          formData.entry[baseName].push(val);
        }
        return;
      }

      // Single fields
      if (field.type === 'radio') {
        const checked = document.querySelector(`[name="${field.name}"]:checked`);
        formData.entry[field.name] = checked ? checked.value : '';
      } else {
        let val = field.value || '';
        if (this.isDateField(field.name)) val = DateUtils.toYYYYMMDD(val);
        formData.entry[field.name] = val;
      }
    });

    // Flatten indexed repeater into entry[baseName] = [v0, v1, v2, ...] (canonical: field_id with array value, no [] in key)
    indexedRepeater.forEach((rowMap, baseName) => {
      const maxIndex = Math.max(...rowMap.keys());
      const arr = [];
      const isDate = this.isDateField(baseName);
      for (let i = 0; i <= maxIndex; i++) {
        let v = rowMap.has(i) ? rowMap.get(i) : '';
        if (isDate && v != null && v !== '') v = DateUtils.toYYYYMMDD(v);
        arr.push(v);
      }
      formData.entry[baseName] = arr;
    });
  }
}